import { getVerticalFullStackOptions } from "@hub/data/data/vertical-full-stack-options";
import { HeadMetadata } from "@hub/models/metadata/head-metadata";
import { Breadcrumb } from "@hub/models/ui/breadcrumb";
import { IOptionCard } from "@hub/models/ui/option-card";
import { PageConfig } from "@hub/models/ui/page-config";
import { hubOriginUrl, hubSiteTree } from "@hub/services/config.service";
import { getBreadCrumbs } from "@hub/services/content/common/get-breadcrumbs";
import { createUrlPath } from "@hub/services/utils/path.utils";
import { Auth0Header } from "@hub/ui/auth0/auth0-header";
import { Breadcrumbs } from "@hub/ui/breadcrumbs/breadcrumbs";
import { BuilderOptions, BuildStep } from "@hub/ui/builder/builder-options";
import { ShellLayout } from "@hub/ui/layouts/shell-layout";
import { GetStaticProps } from "next";
import React from "react";
import styles from "./index.module.css";

interface FullStackVerticalProps {
  canonicalUrl: string;
  breadcrumbs?: Breadcrumb[];
  verticalFullStackOptions: IOptionCard[];
}

export const FullStackVertical: React.FC<FullStackVerticalProps> = ({
  canonicalUrl,
  breadcrumbs,
  verticalFullStackOptions,
}) => {
  const pageConfig: PageConfig = {
    canonicalUrl,
    title: "Auth0 Full-Stack Authentication and Authorization Code Samples",
    description:
      "Learn how to implement authentication and authorization with full-stack applications using different frontend and backend frameworks as well as different levels of access control.",
  };

  const headMetadata: HeadMetadata = {
    title: "Full-Stack Code Samples",
    description:
      "Pick a frontend. Pick a backend. See how Auth0 integrates with a full-stack app of your choice.",
    url: canonicalUrl,
    image:
      "https://cdn.auth0.com/website/developer-center/preview-cards/resources_code-samples_full-stack.png",
    type: "website",
  };

  return (
    <ShellLayout
      header={<Auth0Header />}
      pageConfig={pageConfig}
      headMetadata={headMetadata}
      breadcrumbs={
        breadcrumbs ? <Breadcrumbs breadcrumbs={breadcrumbs} /> : null
      }
      sidebar={
        <div>
          <h2 className={styles.pageTitle}>
            Build a custom full-stack code sample
          </h2>
          <p className={styles.pageDescription}>
            Start by selecting your use case first.
          </p>
          <p className={styles.pageDescription}>
            Then, you can select a security level, a client application type,
            and a server application.
          </p>
        </div>
      }
      document={
        <BuilderOptions
          step={BuildStep.USE_CASE}
          title="Select your use case"
          options={Object.values(verticalFullStackOptions)}
          note={{
            message: `We are working on building code samples covering use cases such as e-Commerce and SaaS applications.`,
            link: {
              label: "Please tell us what use cases you'd like to see.",
              url: "https://community.auth0.com/t/what-use-cases-do-you-want-us-to-cover-with-code-samples-demos-and-guides/80535",
            },
          }}
        />
      }
    />
  );
};

export default FullStackVertical;

export const getStaticProps: GetStaticProps<
  FullStackVerticalProps
> = async () => {
  const breadcrumbs = getBreadCrumbs({
    root: {
      label: hubSiteTree.root.displayName,
      path: hubSiteTree.root.urlPath,
    },
    sections: [
      {
        label: hubSiteTree.root.sections.codeSamples.displayName,
        path: hubSiteTree.root.sections.codeSamples.urlPath,
      },
      {
        label:
          hubSiteTree.root.sections.codeSamples.sections.fullStack.displayName,
        path: hubSiteTree.root.sections.codeSamples.sections.fullStack.urlPath,
      },
    ],
  });

  const canonicalUrl =
    hubOriginUrl +
    createUrlPath([
      hubSiteTree.root.urlPath,
      hubSiteTree.root.sections.codeSamples.urlPath,
      hubSiteTree.root.sections.codeSamples.sections.fullStack.urlPath,
    ]);

  return {
    props: {
      canonicalUrl,
      breadcrumbs,
      verticalFullStackOptions: getVerticalFullStackOptions(),
    },
  };
};
